import { useState, useEffect, useRef } from "react";
import "./QuestionDesign.css";
import { useDispatch, useSelector } from "react-redux";
import { 
    selectEditingForm,
    selectFormThemes,
    selectUIStates,
    selectUser,
    setEditingForm,
    setFormThemes,
    setUIStates,
    setViewingFormThemeSettings,
} from "../../redux/features/appData";
import { 
    Asset,
    CreateFormThemeInWorkspacePayload,
    FormThemeExtended,
    iconPath,
    UpdateFormThemeForFormPayload,
    UpdateFormThemePayload
} from "effno-core";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { FormTheme } from "./FormTheme";
import { FormThemeColorPicker } from "./FormThemeColorPicker";
import { apiCreateFormThemeInWorkspace, apiDeleteFormTheme, apiUpdateFormTheme } from "../../services/FormThemeService";
import { apiFormHasEdits, apiUpdateFormThemeForForm } from "../../services/FormService";
import { AVAILABLE_FONTS, DEFAULT_FONT } from "./fonts";
import { MediaManager } from "../media-manager/MediaManager";
import { parse } from "path";
import { GenericSelect } from "../form-widgets/GenericSelect";
import { ICONS } from "../shared/media";
import { CirclePlus, Trash, Upload } from "lucide-react";

export type FormThemeSettings = {
    font: string;
    questionColor: string;
    answerColor: string;
    backgroundColor: string;
    buttonColor: string;
    buttonTextColor: string;
    backgroundImage: string | undefined;
    imageBrightness: number | undefined;
}

const NEW_FORM_THEME_SETTINGS: FormThemeSettings = {
    font: "Arial",
    questionColor: "#000000",
    answerColor: "#000000",
    backgroundColor: "#FFFFFF",
    buttonColor: "#000000",
    buttonTextColor: "#FFFFFF",
    backgroundImage: undefined,
    imageBrightness: undefined,
}

const FORM_THEME_SETTING_TYPES = {
    font: "font",
    questionColor: "questionColor",
    answerColor: "answerColor",
    backgroundColor: "backgroundColor",
    buttonColor: "buttonColor",
    buttonTextColor: "buttonTextColor",
    backgroundImage: "backgroundImage"
}

type QuestionDesignProps = {
}

export const QuestionDesign = ({} : QuestionDesignProps) => {

    const dispatch = useDispatch();

    const storeUser = useSelector(selectUser);
    const editingForm = useSelector(selectEditingForm);
    const uiStates = useSelector(selectUIStates);
    const formThemes = useSelector(selectFormThemes);

    const componentRef = useRef(null);

    const [editingFormTheme, setEditingFormTheme] = useState<boolean>(editingForm?.formThemeId ? true : false);
    const [formThemeName, setFormThemeName] = useState<string>("");
    const [selectedFormThemeId, setSelecteFormThemeId] = useState<string | undefined>(undefined);
    const [newSettings, setNewSettings] = useState<FormThemeSettings | undefined>(undefined);
    const [revertToSettings, setRevertToSettings] = useState<FormThemeSettings | undefined>(undefined);

    const [mediaManagerOpen, setMediaManagerOpen] = useState<boolean>(false);
    const [brightness, setBrightness] = useState<number>(100);

    useEffect(() => {
        // dispatch(setUIStates({
        //     ...uiStates,
        //     editingFormTheme: false,
        // }));

        if (editingForm && editingForm.formThemeId) {
            handleEditFormTheme(editingForm.formThemeId);
        }

        document.addEventListener("mousedown", eventClick);

        return () => {
            document.removeEventListener("mousedown", eventClick);
        }
        
    }, []);

    // useEffect(() => {
    //     if (editingForm && editingForm.formThemeId && uiStates.openingDesign) {
    //         handleEditFormTheme(editingForm.formThemeId);
    //         dispatch(setUIStates({
    //             ...uiStates,
    //             openingDesign: false
    //         }));
    //     }
    // }, [uiStates.openingDesign])

    useEffect(() => {
        if (editingForm && editingForm.formThemeId) {
            setSelecteFormThemeId(editingForm.formThemeId);
            updateLiveFormTheme(JSON.parse(editingForm?.formTheme?.settings || "{}") as FormThemeSettings);
        }

        if (editingForm && !editingForm.formThemeId && !editingFormTheme) {
            handleAddTheme();
        }

    }, [editingForm]);

    const eventClick = (event: any) => {

        // we need to be in the middle of editing a theme not just vieiwing them all
        if (!(editingForm && editingForm.formThemeId)) {
            return;
        }

        const current : any = componentRef.current;

        if (current && !current.contains(event.target) && editingFormTheme) {
            handleCancelFormThemeEdits();
        }
    }

    // console.log("[QuestionDesign editingQuestion: ", editingQuestion); 
    const handleSelectFormTheme = async (formThemeId: string) => {

        console.log("[QuestionDesign] handleSelectFormTheme/formThemeId: ", formThemeId);
        console.log("[QuestionDesign] handleSelectFormTheme/formThemes: ", formThemes);
        console.log("[QuestionDesign] handleSelectFormTheme/editingForm: ", editingForm);

        if (!formThemes || !editingForm) {
            return;
        }

        if (editingFormTheme) {
            toast("Please save or cancel the current theme before selecting another one.", toastFail);
            return;
        }

        setSelecteFormThemeId(formThemeId);

        const index = formThemes.findIndex((formTheme: FormThemeExtended) => formTheme.id === formThemeId);

        if (index === -1) {
            toast("Failed to find the theme", toastFail);
            return;
        }

        if (!formThemes[index].settings) {
            toast("Failed to find any settings", toastFail);
            return;
        }

        setNewSettings(JSON.parse(formThemes[index].settings || "{}") as FormThemeSettings);

        // update the DB with the new selected theme
        const payload : UpdateFormThemeForFormPayload = {
            formId: editingForm.id,
            formThemeId,
        }

        console.log("[QuestionDesign] handleSelectFormTheme/payload: ", payload);
        const updatedForm = await apiUpdateFormThemeForForm(payload);

        if (!updatedForm) {
            toast("Failed to update the form theme", toastFail);
            return;
        }

        await apiFormHasEdits(updatedForm.id);
        updatedForm.hasEdits = true;

        dispatch(setEditingForm(updatedForm));
    }

    const handleEditFormTheme = (formThemeId: string) => {
        setEditingFormTheme(true);
        setFormThemeName(formThemes?.find((formTheme: FormThemeExtended) => formTheme.id === formThemeId)?.name || "");
        setNewSettings(JSON.parse(formThemes?.find((formTheme: FormThemeExtended) => formTheme.id === formThemeId)?.settings || "{}") as FormThemeSettings);
        setRevertToSettings(JSON.parse(editingForm?.formTheme?.settings || "{}") as FormThemeSettings);
        setSelecteFormThemeId(formThemeId);

        // dispatch(setUIStates({
        //     ...uiStates,
        //     editingFormTheme: true
        // }));
    }

    const handleDeleteFormTheme = async (formThemeId: string) => {
        if (!formThemes) {
            return;
        }

        // clean up redux
        const index = formThemes.findIndex((formTheme: FormThemeExtended) => formTheme.id === formThemeId);

        if (index === -1) {
            toast("Failed to find the theme", toastFail);
            return;
        }

         // pick a new theme if there is one
         const newFormThemeId = formThemes.length > 1 ? formThemes[index === 0 ? 1 : (index - 1)].id : undefined;

         console.log("[QuestionDesign] newFormThemeId: ", newFormThemeId);
 
 
        const deletedTheme = await apiDeleteFormTheme(formThemeId);

        if (!deletedTheme) {
            toast("Problem deleting theme", toastFail);
            return;
        }

        const newFormThemes = formThemes?.filter((formTheme: FormThemeExtended) => formTheme.id !== formThemeId);
        dispatch(setFormThemes(newFormThemes));

        // update the form and what it points to
        if (formThemeId === editingForm?.formThemeId) {
            const payload : UpdateFormThemeForFormPayload = {
                formId: editingForm.id,
                formThemeId: newFormThemeId,
            }

            const updatedForm = await apiUpdateFormThemeForForm(payload);

            if (!updatedForm) {
                toast("Failed to update the form theme", toastFail);
                return;
            }

            await apiFormHasEdits(updatedForm.id);
            updatedForm.hasEdits = true;

            dispatch(setEditingForm(updatedForm));
        }

        // updated what we are pointing to
        setSelecteFormThemeId(newFormThemeId);

    }

    const handleUnselectFormTheme = async() => {
        if (!editingForm) {
            return;
        }

        // if (!!true) {
        //     alert("foo")
        //     return;
        // }

        const payload : UpdateFormThemeForFormPayload = {
            formId: editingForm.id,
            formThemeId: undefined,
        }

        const updatedForm = await apiUpdateFormThemeForForm(payload);

        if (!updatedForm) {
            toast("Failed to update the form theme", toastFail);
            return;
        }

        await apiFormHasEdits(updatedForm.id);
        updatedForm.hasEdits = true;

        dispatch(setEditingForm(updatedForm));
        setSelecteFormThemeId(undefined);
    }

    const handleAddTheme = () => {
        if (editingFormTheme) {
            toast("You are already editing a theme", toastFail);
            return;
        }

        console.log("[QuestionDesign] handleAddTheme/editingForm: ", editingForm);

        setEditingFormTheme(true);
        setSelecteFormThemeId(undefined);
        setFormThemeName("My theme");
        setNewSettings({...NEW_FORM_THEME_SETTINGS})
        setRevertToSettings(editingForm?.formTheme ? 
            JSON.parse(editingForm?.formTheme?.settings || "{}") as FormThemeSettings :
            undefined
        );

        // dispatch(setUIStates({
        //     ...uiStates,
        //     editingFormTheme: true
        // }));
    }

    const handleColor = (type: string, newValue: string) => {
        if (!newSettings) {
            return;
        }

        const newSettingsCopy : any = {...newSettings};
        newSettingsCopy[type] = newValue;

        console.log("[QuestionDesign] type: ", type);
        console.log("[QuestionDesign] newValue: ", newValue);
        console.log("[QuestionDesign] newSettingsCopy: ", newSettingsCopy);
        setNewSettings(newSettingsCopy as FormThemeSettings);
        updateLiveFormTheme(newSettingsCopy);
    }

    const handleColorComplete = () => {
        // do nothing
    }

    const updateExistingFormTheme =  async () => {

        if (!selectedFormThemeId || !newSettings || !formThemes) {
            toast("Please select a theme and make some changes", toastFail);
            return;
        }

        console.log("[QuestionDesign/updateExistingFormTheme] newSettings: ", newSettings);

        const payload : UpdateFormThemePayload = {
            formThemeId: selectedFormThemeId,
            name: formThemeName,
            settings: JSON.stringify(newSettings)
        }

        console.log("[QuestionDesign/updateExistingFormTheme] payload: ", payload);

        const updatedTheme = await apiUpdateFormTheme(payload);

        if (!updatedTheme) {
            toast("Failed to update the theme", toastFail);
            return;
        }

        const index = formThemes?.findIndex((formTheme: FormThemeExtended) => formTheme.id === selectedFormThemeId);

        if (index === -1) {
            toast("Failed to find the theme", toastFail);
            return;
        }
       
        const newFormThemes = [...formThemes];
        newFormThemes[index] = updatedTheme;
        dispatch(setFormThemes(newFormThemes));
        setRevertToSettings({...newSettings} as FormThemeSettings);

        // update the editing form
        if (editingForm && editingForm.formThemeId === selectedFormThemeId) {
            const updatedForm = {
                ...editingForm,
                formThemeId: updatedTheme.id,
                formTheme: updatedTheme,
            }

            await apiFormHasEdits(updatedForm.id);
            updatedForm.hasEdits = true;

            dispatch(setEditingForm(updatedForm));
        }

        toast("Theme updated", toastSuccess);
    }

    // if we save a new form theme, we need to pick it
    const saveNewFormTheme =  async () => {
        if (!newSettings || !storeUser?.currentWorkspaceId || !editingForm) {
            toast("No theme settings", toastFail);
            return;
        }
    
        console.log("[QuestionDesign/saveNewFormTheme] newSettings: ", newSettings);

        const payload : CreateFormThemeInWorkspacePayload = {
            workspaceId: storeUser.currentWorkspaceId,
            formId: editingForm.id,
            name: formThemeName,
            settings: JSON.stringify(newSettings)
        }

        console.log("[QuestionDesign/saveNewFormTheme] payload: ", payload);

        const newTheme = await apiCreateFormThemeInWorkspace(payload);

        if (!newTheme) {
            toast("Failed to create theme", toastFail);
            return;
        }

        const updatedFormThemes = formThemes ? [...formThemes, newTheme] : [newTheme];

        // dispatch(setFormThemes(formThemes ? [...formThemes, newTheme] : [newTheme]));
        dispatch(setFormThemes(updatedFormThemes));

        setSelecteFormThemeId(newTheme.id);
        setEditingFormTheme(false);

        // setSelecteFormThemeId(formThemeId);

        const index = updatedFormThemes.findIndex((formTheme: FormThemeExtended) => formTheme.id === newTheme.id);

        if (index === -1) {
            toast("Failed to find the theme", toastFail);
            return;
        }

        if (!updatedFormThemes[index].settings) {
            toast("Failed to find any settings", toastFail);
            return;
        }

        setNewSettings(JSON.parse(updatedFormThemes[index].settings || "{}") as FormThemeSettings);

        // update the DB with the new selected theme
        const updatePayload : UpdateFormThemeForFormPayload = {
            formId: editingForm.id,
            formThemeId: newTheme.id,
        }

        console.log("[QuestionDesign] handleSelectFormTheme/updatePayload: ", updatePayload);
        const updatedForm = await apiUpdateFormThemeForForm(updatePayload);

        if (!updatedForm) {
            toast("Failed to update the form theme", toastFail);
            return;
        }

        await apiFormHasEdits(updatedForm.id);
        updatedForm.hasEdits = true;

        dispatch(setEditingForm(updatedForm));

        // handleSelectFormTheme(newTheme.id);

        toast("New theme created and selected", toastSuccess);
    }

    const handleCancelFormThemeEdits = () => {
        // alert("Cancelling")
        setEditingFormTheme(false);
        setNewSettings(undefined);
        setSelecteFormThemeId(editingForm?.formThemeId)
        dispatch(setViewingFormThemeSettings(revertToSettings));
    }

    const handleSaveFormTheme = async () => {
        if (!newSettings || !formThemeName) {
            return;
        }

        if (selectedFormThemeId) {
            await updateExistingFormTheme();
            return;  
        }

        await saveNewFormTheme();
    }

    const updateLiveFormTheme = (formThemeSettings: FormThemeSettings) => {
        dispatch(setViewingFormThemeSettings(formThemeSettings));
    }

    const handleChangeBackgroundImage =  async (asset: Asset) => {
        setMediaManagerOpen(false);

        console.log("[QuestionDesign] change to asset: ", asset);
        
        if (!newSettings) {
            return;
        }

        const newSettingsCopy : any = {...newSettings, backgroundImage: asset.URL};

        setNewSettings(newSettingsCopy as FormThemeSettings);
        updateLiveFormTheme(newSettingsCopy);
    }

    // console.log("[QuestionDesign] newSettings: ", newSettings);
    // console.log("[QuestionDesign] editingFormTheme: ", editingFormTheme);
    // console.log("[QuestionDesign] selectedFormThemeId: ", selectedFormThemeId);
    // console.log("[QuestionDesign] formThemes: ", formThemes);
    console.log("[QuestionDesign] editingFormTheme: ", editingFormTheme);



    const formThemesSorted = formThemes ? [...formThemes] : [];
    const sorted = formThemesSorted ? formThemesSorted.sort((a: FormThemeExtended, b: FormThemeExtended) => {
        return a.id === selectedFormThemeId ? -1 : 0
    }) : [];

    const fontOptions = AVAILABLE_FONTS.sort((a: string, b: string) => {
        return a.toLowerCase().localeCompare(b.toLowerCase())
    });

    // console.log("[QuestionDesign] sorted: ", sorted);

    return (        
        <div 
            className="question-design"
            ref={componentRef}
        >

            <div className="question-design-title">
                Design
            </div>
            {/* <button
                onClick={handleAddTheme}
            >
                + Add
            </button> */}

            {!editingFormTheme && formThemesSorted && formThemesSorted.length > 0 && 
                <div className="question-design-my-themes">
                    <div 
                        className="question-design-add-design"
                        onClick={handleAddTheme}
                    >
                        <CirclePlus size={12} color="grey" />
                        <span>New Theme</span>
                    </div>
                    {formThemesSorted.map((formTheme: FormThemeExtended, index) => (
                            <FormTheme 
                                key={"form-theme-" + formTheme.id}
                                formTheme={formTheme}
                                isSelected={selectedFormThemeId === formTheme.id}
                                onChange={handleSelectFormTheme}
                                onEdit={handleEditFormTheme}
                                onDelete={handleDeleteFormTheme}
                                onUnselect={handleUnselectFormTheme}
                            />
                        ))
                    }

                </div>
            }

            {editingFormTheme && 
                <div 
                    className="form-theme-editor"
                >
                    <div className="form-theme-editor-name">
                        {/* Name: */}
                        <input 
                            type="text"
                            value={formThemeName}
                            autoFocus={true}
                            onChange={(event) => {
                                setFormThemeName(event.target.value)
                                updateLiveFormTheme({
                                    ...newSettings,
                                    name: event.target.value
                                } as FormThemeSettings);
                            }}
                        />
                    </div>

                    

                    <div className="form-theme-editor-setting">
                        <div className="form-theme-editor-setting-label">
                            <label>Questions</label>
                        </div>
                        <div className="form-theme-editor-setting-input">
                            <FormThemeColorPicker 
                                key={FORM_THEME_SETTING_TYPES.questionColor}
                                color={newSettings?.questionColor}
                                type={FORM_THEME_SETTING_TYPES.questionColor}
                                onChange={handleColor}
                                onComplete={handleColorComplete}
                            />
                        </div>
                    </div>

                    <div className="form-theme-editor-setting">
                        <div className="form-theme-editor-setting-label">
                            <label>Answers</label>
                        </div>
                        <div className="form-theme-editor-setting-input">
                            <FormThemeColorPicker 
                                key={FORM_THEME_SETTING_TYPES.answerColor}
                                color={newSettings?.answerColor}
                                type={FORM_THEME_SETTING_TYPES.answerColor}
                                onChange={handleColor}
                                onComplete={handleColorComplete}

                            />
                        </div>
                    </div>

                    

                    <div className="form-theme-editor-setting">
                        <div className="form-theme-editor-setting-label">
                            <label>Buttons</label>
                        </div>
                        <div className="form-theme-editor-setting-input">
                            <FormThemeColorPicker 
                                key={FORM_THEME_SETTING_TYPES.buttonColor}
                                color={newSettings?.buttonColor}
                                type={FORM_THEME_SETTING_TYPES.buttonColor}
                                onChange={handleColor}
                                onComplete={handleColorComplete}

                            />
                        </div>
                    </div>

                    <div className="form-theme-editor-setting">
                        <div className="form-theme-editor-setting-label">
                            <label>Button Text</label>
                        </div>
                        <div className="form-theme-editor-setting-input">
                            <FormThemeColorPicker 
                                key={FORM_THEME_SETTING_TYPES.buttonTextColor}
                                color={newSettings?.buttonTextColor}
                                type={FORM_THEME_SETTING_TYPES.buttonTextColor}
                                onChange={handleColor}
                                onComplete={handleColorComplete}
                            />
                        </div>
                    </div>

                    <div className="form-theme-editor-setting">
                        <div className="form-theme-editor-setting-label">
                            <label>Background</label>
                        </div>
                        <div className="form-theme-editor-setting-input">
                            <FormThemeColorPicker 
                                key={FORM_THEME_SETTING_TYPES.backgroundColor}
                                color={newSettings?.backgroundColor}
                                type={FORM_THEME_SETTING_TYPES.backgroundColor}
                                onChange={handleColor}
                                onComplete={handleColorComplete}

                            />
                        </div>
                    </div>

                    <div className="form-theme-editor-setting">
                        <div className="form-theme-editor-setting-label">
                            <label>Background Image</label>
                        </div>
                        <div 
                            className="form-theme-editor-setting-input"
                        >
                            {newSettings?.backgroundImage &&
                                <div 
                                    className="form-theme-editor-setting-input-delete"
                                    onClick={() => {
                                        const updatedSettings = {
                                            ...newSettings,
                                            backgroundImage: undefined
                                        } as FormThemeSettings
        
                                        setNewSettings(updatedSettings);
                                        updateLiveFormTheme(updatedSettings);
                                    }}
                                >
                                    <Trash size={16} />          
                                </div>
                                
                            }
                            {!newSettings?.backgroundImage &&
                                <div className="form-theme-editor-setting-add-image"
                                    onClick={()=> setMediaManagerOpen(true)}
                                    style={{verticalAlign: "middle"}}
                                >
                                    <Upload size={16} />
                                    {/* <img src={iconPath("add.svg", storeUser)} /> */}
                                </div>
                            }
                            
                        
                            
                        </div>
                    </div>

                    

                    {newSettings?.backgroundImage && 
                        <div className="form-theme-editor-setting">

                            <div className="form-theme-editor-background-image">
                                <img src={newSettings.backgroundImage} />
                            </div>
                            
                            <div 
                                className="form-theme-editor-setting-input"
                            >
                                <div className="form-theme-editor-setting-input-label">
                                    Brightness
                                </div>
                                
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={brightness}
                                    onChange={(e: any) => {
                                        setBrightness(e.target.value);
                                        const newBrightness = parseFloat(e.target.value) / 100;
                                        const updatedSettings = {
                                            ...newSettings,
                                            imageBrightness: newBrightness,
                                        } as FormThemeSettings
        
                                        setNewSettings(updatedSettings);
                                        updateLiveFormTheme(updatedSettings);
                                    }}
                                />
                                {/* <br/>
                                <input 
                                    type="text"
                                    value={newSettings?.imageBrightness}
                                    onChange={(e: any) => {
                                        // alert(parseFloat(e.target.value));
                                        const updatedSettings = {
                                            ...newSettings,
                                            imageBrightness: parseFloat(e.target.value) ? e.target.value : undefined,
                                        } as FormThemeSettings
        
                                        setNewSettings(updatedSettings);
                                        updateLiveFormTheme(updatedSettings);
                                    }}
                                    
                                /> */}
                            </div>
                        </div>
                    }

                    <span style={{marginTop: "8px"}}>Select a Font</span>

                    <div className="form-theme-editor-font">
                            
                            <GenericSelect
                                options={fontOptions.map((font: string) => {
                                    return {
                                        value: font,
                                        displayName: font
                                    }
                                })}
                                selected={{ value: newSettings?.font, displayName: newSettings?.font || DEFAULT_FONT }}
                                onChange={(font) => {
                                    const updatedSettings = {
                                        ...newSettings,
                                        font: font || DEFAULT_FONT
                                    } as FormThemeSettings

                                    setNewSettings(updatedSettings);
                                    updateLiveFormTheme(updatedSettings);
                                }}
                                isFont={true}
                                maxHeight={"160px"}
                            />
                        
                    </div>

                    <div className="form-theme-editor-actions">
                        <button
                            className="button-cancel"
                            onClick={handleCancelFormThemeEdits}
                        >
                            Switch
                        </button>
                        <button
                            className="button-save"
                            style={{marginBottom: "24px"}}
                            onClick={handleSaveFormTheme}
                        >
                            {selectedFormThemeId ? "Save" : "Save"}
                        </button>
                    </div>
                </div>

            }

            {mediaManagerOpen &&
                <MediaManager 
                    isOpen={mediaManagerOpen}
                    onClose={() => setMediaManagerOpen(false)}
                    onSelectedAsset={handleChangeBackgroundImage}
                />
            }
            
        </div>
    )
}